import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useCustomers, saveCustomers, issueClientCode, customersCsv,
  customerMatches, CRM_FIELDS, type Customer,
} from '../lib/csrSuite';
import { useRequests, isRequestOpen, todayPacific, type CsrRequest } from '../lib/csrRequestsLive';
import { useSuiteQuotes, type SuiteQuote } from '../lib/csrCatalog';

const keyOf = (c: Customer) => c.code || c.n;
const initials = (name: string) =>
  (name || '?').replace(/[^A-Za-z0-9 ]/g, '').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

function cleanForm(f: Customer): Customer {
  const o: Customer = { n: (f.n || '').trim() };
  for (const field of CRM_FIELDS) {
    const v = (f[field.k] || '').toString().trim();
    if (v) o[field.k] = v;
  }
  o.n = (f.n || '').trim();
  return o;
}

export default function CustomerCRM() {
  const { customers, meta, loading, live, error } = useCustomers();
  const { requests } = useRequests();
  const { quotes } = useSuiteQuotes();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [selKey, setSelKey] = useState<string | null>(null);
  const [form, setForm] = useState<Customer | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [msg, setMsg] = useState('');
  const loadedKey = useRef<string | null>(null);
  const [urlParams] = useSearchParams();

  useEffect(() => {
    const s = urlParams.get('sel');
    if (s) setSelKey(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selKey === loadedKey.current) return;
    loadedKey.current = selKey;
    const c = customers.find((x) => keyOf(x) === selKey);
    setForm(c ? { ...c } : null);
    setStatus('idle');
  }, [selKey, customers]);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return customers.map((c, i) => ({ c, i })).filter(({ c }) => customerMatches(c, t));
  }, [customers, q]);

  const setField = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((p) => (p ? { ...p, [k]: e.target.value } : p));

  async function persist(next: Customer[], note = 'Saved') {
    setStatus('saving');
    try {
      await saveCustomers(next);
      setStatus('saved'); setMsg(note);
      setTimeout(() => setStatus('idle'), 1600);
    } catch (e) {
      setStatus('error');
      setMsg((e as Error)?.message?.includes('permission') ? 'Sign in with your Microflex account to save changes.' : ((e as Error)?.message || 'Save failed'));
    }
  }

  async function onSave() {
    if (!form) return;
    if (!form.n?.trim()) { setStatus('error'); setMsg('Company Name is required.'); return; }
    const cleaned = cleanForm(form);
    const next = customers.map((c) => (keyOf(c) === selKey ? cleaned : c));
    if (!next.some((c) => keyOf(c) === keyOf(cleaned))) next.unshift(cleaned);
    setSelKey(keyOf(cleaned)); loadedKey.current = keyOf(cleaned);
    await persist(next, 'Customer saved');
  }

  async function onAdd() {
    const { code } = await issueClientCode(meta).catch(() => ({ code: '' }));
    const fresh: Customer = { n: 'New Customer', ...(code ? { code } : {}) };
    const next = [fresh, ...customers];
    setQ('');
    setSelKey(keyOf(fresh)); loadedKey.current = keyOf(fresh);
    setForm({ ...fresh });
    await persist(next, 'Customer added');
  }

  async function onDelete() {
    if (!form) return;
    if (!window.confirm(`Delete "${form.n}" from the customer list?\n(Export CSV first if you want a backup.)`)) return;
    const next = customers.filter((c) => keyOf(c) !== selKey);
    setSelKey(null); setForm(null); loadedKey.current = null;
    await persist(next, 'Customer deleted');
  }

  function onExport() {
    const blob = new Blob([customersCsv(customers)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'microflex-customers.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  function onQuote() {
    if (!form) return;
    const p = new URLSearchParams();
    if (form.n) p.set('company', form.n);
    if (form.ct) p.set('attention', form.ct);
    if (form.rp) p.set('salesRep', form.rp);
    nav(`/quote-builder?${p.toString()}`);
  }
  function onLogRequest() {
    if (!form) return;
    nav(`/requests?client=${encodeURIComponent(form.n)}`);
  }

  return (
    <>
      <style>{css}</style>
      <div className="page-head">
        <div>
          <div className="page-title">Customer CRM</div>
          <div className="page-sub">{customers.length} customers · the shared estimating-suite roster. {live ? 'Live' : loading ? 'Loading…' : 'Offline'}.</div>
        </div>
        <div className="cc-actions">
          <button className="btn small" onClick={onAdd}>＋ Add Customer</button>
          <button className="btn small" onClick={onExport}>⭳ Export CSV</button>
        </div>
      </div>

      {error && <div className="cc-warn">Couldn’t load the roster: {error}</div>}

      <div className="cc-grid">
        <div className="card cc-list">
          <div className="cc-search"><input placeholder="🔎 Search name, contact, phone, rep…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          <div className="cc-rows">
            {loading ? <div className="cc-empty">Loading…</div> : shown.length === 0 ? <div className="cc-empty">No matches</div> :
              shown.map(({ c }) => {
                const k = keyOf(c);
                const meta2 = [c.ct, c.ph, c.rp ? `Rep ${c.rp}` : ''].filter(Boolean).join(' · ') || 'no contact on file';
                return (
                  <div key={k} className={`cc-row${k === selKey ? ' active' : ''}`} onClick={() => setSelKey(k)}>
                    <div className="cc-nm">{c.n}{c.tm ? <span className="tag">{c.tm}</span> : null}</div>
                    <div className="cc-meta">{meta2}</div>
                  </div>
                );
              })}
          </div>
        </div>

        <div className="card cc-detail">
          {!form ? (
            <div className="cc-empty big">📇<div>Select a customer to view &amp; edit,<br />or add a new one.</div></div>
          ) : (
            <>
              <div className="cc-dh">
                <div className="cc-av">{initials(form.n)}</div>
                <h2>{form.n || '—'}</h2>
                <div className={`cc-status ${status}`}>
                  {status === 'saving' ? 'Saving…' : status === 'saved' ? `✔ ${msg}` : status === 'error' ? `⚠ ${msg}` : '✔ Saved'}
                </div>
              </div>
              <div className="cc-fields">
                {CRM_FIELDS.map((f) => (
                  <label key={f.k} className={`cc-f${f.full ? ' wide' : ''}`}>
                    <span className="cc-l">{f.label}</span>
                    {f.area
                      ? <textarea rows={3} value={form[f.k] || ''} onChange={setField(f.k)} />
                      : <input value={form[f.k] || ''} onChange={setField(f.k)} />}
                  </label>
                ))}
              </div>

              <div className="cc-subhead">Requests, quotes &amp; insights
                <button className="btn small" style={{ marginLeft: 'auto' }} onClick={onLogRequest}>＋ Log Request</button>
              </div>
              <ClientInsights customer={form} requests={requests} quotes={quotes} />

              <div className="cc-savebar">
                <button className="btn small primary" onClick={onSave} disabled={status === 'saving'}>💾 Save Changes</button>
                <button className="btn small" onClick={onQuote}>🧮 Quote this customer</button>
                <button className="btn small danger" style={{ marginLeft: 'auto' }} onClick={onDelete}>🗑 Delete</button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function daysBetween(a: string, b: string): number { return Math.round((new Date(b + 'T12:00:00').getTime() - new Date(a + 'T12:00:00').getTime()) / 86400000); }

function ClientInsights({ customer, requests, quotes }: { customer: Customer; requests: CsrRequest[]; quotes: SuiteQuote[] }) {
  const t = todayPacific();
  const c = customer;
  const reqs = requests.filter((r) => r.client === c.n);
  const cq = quotes.filter((x) => (x.client || '') === c.n);
  const wonQ = cq.filter((x) => x.status === 'Won');
  const lostQ = cq.filter((x) => x.status === 'Lost');
  const openQ = cq.filter((x) => ['In Progress', 'Needs Attention', 'Sent'].includes(x.status || ''));
  const wonVal = wonQ.reduce((s2, x) => s2 + (x.value || 0), 0);
  const pipeVal = openQ.reduce((s2, x) => s2 + (x.value || 0), 0);
  const winRate = (wonQ.length + lostQ.length) > 0 ? Math.round((wonQ.length / (wonQ.length + lostQ.length)) * 100) : null;
  const fm = (v: number) => '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  const open = reqs.filter((r) => isRequestOpen(r.status));
  const overdue = open.filter((r) => r.due && r.due < t);
  const stale = open.filter((r) => r.status === 'New' && r.date && daysBetween(r.date, t) >= 5);
  const last = reqs.reduce((m, r) => ((r.date || '') > m ? (r.date as string) : m), '');
  const ro = reqs.filter((r) => (r.types || []).some((x) => /reorder/i.test(x)) && r.date).map((r) => r.date as string).sort();
  let cadence: number | null = null; let nextRo: string | null = null;
  if (ro.length >= 2) {
    let s = 0; for (let i = 1; i < ro.length; i++) s += daysBetween(ro[i - 1], ro[i]);
    cadence = Math.max(1, Math.round(s / (ro.length - 1)));
    const d = new Date(ro[ro.length - 1] + 'T12:00:00'); d.setDate(d.getDate() + cadence);
    nextRo = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }
  // ---- health score + completeness ----
  const FKEYS: [string, string][] = [['ct', 'Primary Contact'], ['ph', 'Phone'], ['email', 'Email'], ['tm', 'Payment Terms'], ['rp', 'Sales Rep'], ['bt', 'Bill To'], ['sh', 'Ship To']];
  const missing = FKEYS.filter(([k]) => !c[k]);
  const comp = Math.round(25 * (FKEYS.length - missing.length) / FKEYS.length);
  let rec = 0;
  if (last) { const d = daysBetween(last, t); rec = d <= 14 ? 35 : d <= 30 ? 28 : d <= 60 ? 20 : d <= 90 ? 12 : d <= 180 ? 6 : 2; }
  const execScore = reqs.length ? Math.max(0, 25 - (overdue.length * 6 + stale.length * 3)) : 12;
  const com = Math.min(15, wonQ.length * 6 + openQ.length * 3);
  const score = Math.max(0, Math.min(100, comp + rec + execScore + com));
  const grade: [string, string] = score >= 80 ? ['EXCELLENT', 'var(--gn)'] : score >= 60 ? ['GOOD', 'var(--ac)'] : score >= 40 ? ['NEEDS ATTENTION', 'var(--yl)'] : ['AT RISK', 'var(--rd)'];
  // ---- relationship stats ----
  const dates = reqs.map((r) => r.date).filter(Boolean).sort() as string[];
  const first = dates[0] || '';
  let pace = '';
  if (first && last && first !== last) { const span = Math.max(30, daysBetween(first, last)); pace = (reqs.length / (span / 30)).toFixed(1) + ' req/mo'; }
  else if (reqs.length) pace = reqs.length + ' total';
  const modeOf = (arr: (string | undefined)[]) => { const m: Record<string, number> = {}; arr.forEach((x) => { if (x) m[x] = (m[x] || 0) + 1; }); const e = Object.entries(m).sort((a, b) => b[1] - a[1]); return e.length ? e[0][0] : ''; };
  const topHandler = modeOf(reqs.map((r) => r.assignee));
  const channel = modeOf(reqs.map((r) => r.method));
  const counts: Record<string, number> = {};
  requests.forEach((r) => { if (r.client) counts[r.client] = (counts[r.client] || 0) + 1; });
  const myCount = counts[c.n] || 0;
  let rankTxt = '';
  if (myCount) { const rank = 1 + Object.values(counts).filter((v) => v > myCount).length; const tot = Object.keys(counts).length; const pct = Math.round(100 * (tot - rank) / Math.max(1, tot - 1)); rankTxt = `#${rank} of ${tot} active customers · more active than ${pct}%`; }
  // ---- next best actions ----
  const acts: ReactNode[] = [];
  if (overdue.length) { const o = [...overdue].sort((a, b) => (a.due || '').localeCompare(b.due || ''))[0]; acts.push(<>Clear <b>{overdue.length} overdue</b> — oldest: “{String(o.desc || o.id).slice(0, 60)}” (due {o.due})</>); }
  if (nextRo && nextRo < t) acts.push(<><b>Call about a reorder</b> — past their ~{cadence}-day cycle (expected {nextRo})</>);
  if (stale.length) acts.push(<>Follow up on <b>{stale.length} untouched “New”</b> request{stale.length > 1 ? 's' : ''}</>);
  if (openQ.length) acts.push(<>Follow up on <b>{openQ.length} active quote{openQ.length > 1 ? 's' : ''}</b> ({fm(pipeVal)} open)</>);
  if (!reqs.length) acts.push(<><b>No history yet</b> — log their first request or start intro outreach</>);
  if (!c.email) acts.push(<>Collect an <b>email address</b> — it prints on the branded quote</>);
  if (!c.ct && !c.ph) acts.push(<>Add a <b>contact &amp; phone</b> so the team can reach them</>);
  if (last && daysBetween(last, t) > 90) acts.push(<><b>Win-back outreach</b> — quiet for {daysBetween(last, t)} days</>);
  const topActs = acts.slice(0, 3);
  const mixMap: Record<string, number> = {};
  reqs.forEach((r) => (r.types || []).forEach((x) => { mixMap[x] = (mixMap[x] || 0) + 1; }));
  const mix = Object.entries(mixMap).sort((a, b) => b[1] - a[1]).slice(0, 3);
  const list = [...reqs].sort((a, b) => {
    const ao = isRequestOpen(a.status) ? 0 : 1; const bo = isRequestOpen(b.status) ? 0 : 1;
    if (ao !== bo) return ao - bo; return (b.date || '').localeCompare(a.date || '');
  });
  return (<>
    <div className="ci-chips">
      <div className="ci-health" style={{ borderColor: grade[1] }} title={`Profile ${comp}/25 · Recency ${rec}/35 · Execution ${execScore}/25 · Commercial ${com}/15`}>
        <b style={{ color: grade[1] }}>{score}<span>/100</span></b>
        <em style={{ color: grade[1] }}>{grade[0]}</em>
        <span className="ci-hl">health score ⓘ</span>
      </div>
      <div className="ci-chip"><b>{open.length}</b><span>Open</span></div>
      <div className="ci-chip"><b style={overdue.length ? { color: 'var(--rd)' } : undefined}>{overdue.length}</b><span>Overdue</span></div>
      <div className="ci-chip"><b>{reqs.length}</b><span>Total</span></div>
      <div className="ci-chip"><b>{cq.length}</b><span>Quotes</span></div>
      <div className="ci-chip"><b>{last ? last.slice(5) : '—'}</b><span>Last request</span></div>
    </div>
    {missing.length > 0 && (
      <div className="ci-missing">⚠ Profile {FKEYS.length - missing.length}/{FKEYS.length} — missing: {missing.map(([k, l]) => <span key={k}>{l}</span>)}</div>
    )}
    {topActs.length > 0 && (
      <div className="ci-acts">
        <div className="ci-acts-h">🎯 Next best actions</div>
        {topActs.map((a, i) => <div key={i}>{i + 1}. {a}</div>)}
      </div>
    )}
    {(overdue.length > 0 || stale.length > 0 || nextRo || mix.length > 0 || cq.length > 0) && (
      <div className="ci-flags">
        {overdue.length > 0 && <div>🔴 <b>{overdue.length} overdue</b> — needs action now</div>}
        {stale.length > 0 && <div>🟡 <b>{stale.length}</b> still “New” after 5+ days with no movement</div>}
        {nextRo && (nextRo < t
          ? <div>🟠 <b>Reorder likely due</b> — expected {nextRo} (orders ~every {cadence}d)</div>
          : <div>🔵 Next reorder expected ~<b>{nextRo}</b> (orders ~every {cadence}d)</div>)}
        {cq.length > 0 && <div>💰 Quotes: <b>{wonQ.length} won</b>{wonVal > 0 ? ` (${fm(wonVal)})` : ''}{winRate != null ? ` · ${winRate}% win rate` : ''}{pipeVal > 0 ? <span> · <b>{fm(pipeVal)}</b> open pipeline</span> : null}{openQ.length > 0 ? ` · ${openQ.length} active` : ''}</div>}
        {mix.length > 0 && <div className="ci-mix">Request mix: {mix.map(([k, v]) => `${k} ×${v}`).join(' · ')}</div>}
      </div>
    )}
    {(first || pace || channel || topHandler || rankTxt) && (
      <div className="ci-rel">
        {[first ? `Customer since ${first}` : '', pace ? `Pace ${pace}` : '', channel ? `Usually via ${channel}` : '', topHandler ? `Handled by ${topHandler}` : '', rankTxt].filter(Boolean).join('  ·  ')}
      </div>
    )}
    <div className="ci-list">
      {list.slice(0, 8).map((r) => {
        const od = isRequestOpen(r.status) && r.due && r.due < t;
        return (
          <div key={r.id} className="ci-row">
            <div className="ci-desc">{r.desc || (r.types || []).join(', ') || 'Request'} <span className="ci-id">{r.id}</span></div>
            <div className="ci-meta">{(r.types || []).join(', ') || 'Request'} · {r.date || '—'} · {r.assignee || '—'}{r.due ? <span style={od ? { color: 'var(--rd)', fontWeight: 700 } : undefined}> · due {r.due}</span> : null} · <b>{r.status}</b></div>
          </div>
        );
      })}
      {list.length > 8 && <div className="ci-more">+ {list.length - 8} more in the Request Log</div>}
    </div>
  </>);
}
const css = `
.cc-actions{display:flex;gap:10px}
.cc-actions .btn,.cc-savebar .btn,.cc-subhead .btn{width:auto;padding:8px 14px;font-size:13px}
.cc-warn{background:rgba(234,179,8,.12);border:1px solid rgba(234,179,8,.4);color:var(--yl);border-radius:10px;padding:10px 14px;font-size:13px;margin:10px 0}
.cc-grid{display:grid;grid-template-columns:320px 1fr;gap:16px;align-items:start;margin-top:8px}
@media(max-width:900px){.cc-grid{grid-template-columns:1fr}}
.cc-list{padding:12px}
.cc-search input{margin-bottom:10px}
.cc-rows{max-height:70vh;overflow:auto;display:flex;flex-direction:column;gap:2px}
.cc-row{padding:10px 12px;border-radius:8px;cursor:pointer;border:1px solid transparent}
.cc-row:hover{background:var(--bg2)}
.cc-row.active{background:rgba(0,168,207,.12);border-color:rgba(0,168,207,.4)}
.cc-nm{font-weight:700;font-size:13px;color:var(--tx);display:flex;align-items:center;gap:8px}
.cc-nm .tag{font-size:10px;padding:1px 7px}
.cc-meta{font-size:11px;color:var(--tx3);margin-top:2px}
.cc-detail{padding:18px;min-height:320px}
.cc-empty{color:var(--tx3);text-align:center;padding:30px;font-size:13px}
.cc-empty.big{font-size:34px;display:flex;flex-direction:column;align-items:center;gap:12px}
.cc-empty.big div{font-size:13px}
.cc-dh{display:flex;align-items:center;gap:12px;margin-bottom:14px}
.cc-av{width:40px;height:40px;border-radius:10px;background:rgba(0,168,207,.16);color:var(--ac);display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px}
.cc-dh h2{margin:0;font-size:18px}
.cc-status{margin-left:auto;font-size:12px;font-weight:600;color:var(--tx3)}
.cc-status.saved{color:var(--gn)}.cc-status.error{color:var(--rd)}.cc-status.saving{color:var(--ac)}
.cc-fields{display:grid;grid-template-columns:1fr 1fr;gap:10px 14px}
.cc-f{display:flex;flex-direction:column;gap:5px}
.cc-f.wide{grid-column:span 2}
.cc-l{font-size:11px;color:var(--tx3);font-weight:600}
.cc-subhead{display:flex;align-items:center;gap:10px;font-weight:700;font-size:13px;margin:16px 0 8px;color:var(--tx2)}
.cc-reqnote{font-size:12px;color:var(--tx3);margin-bottom:10px}
.cc-savebar{display:flex;align-items:center;gap:10px;border-top:1px solid var(--bdr);padding-top:14px;margin-top:8px}
.btn.danger{color:var(--rd);border-color:rgba(239,68,68,.4)}
.ci-chips{display:flex;gap:8px;flex-wrap:wrap;margin:4px 0 8px}
.ci-chip{flex:1;min-width:80px;background:var(--bg2);border:1px solid var(--bdr);border-radius:10px;padding:8px 10px;text-align:center}
.ci-chip b{display:block;font-size:17px}
.ci-chip span{font-size:10px;color:var(--tx3);text-transform:uppercase;letter-spacing:.3px}
.ci-flags{background:rgba(0,168,207,.07);border:1px solid rgba(0,168,207,.25);border-radius:10px;padding:9px 12px;font-size:12px;line-height:1.8;margin-bottom:10px}
.ci-mix{color:var(--tx3)}
.ci-list{display:flex;flex-direction:column;margin-bottom:6px}
.ci-row{padding:7px 10px;border-radius:8px;border:1px solid var(--bdr);margin-bottom:4px}
.ci-desc{font-weight:700;font-size:12.5px}
.ci-id{color:var(--tx3);font-weight:500;font-size:10px}
.ci-meta{font-size:11px;color:var(--tx3);margin-top:2px}
.ci-more{font-size:11px;color:var(--tx3);padding:4px 10px}
.ci-health{min-width:120px;background:var(--bg2);border:1px solid var(--bdr);border-radius:10px;padding:8px 12px;text-align:center;display:flex;flex-direction:column;gap:1px}
.ci-health b{font-size:20px;line-height:1.1}
.ci-health b span{font-size:11px;color:var(--tx3);font-weight:600}
.ci-health em{font-style:normal;font-size:9.5px;font-weight:800;letter-spacing:.5px}
.ci-hl{font-size:9.5px;color:var(--tx3)}
.ci-missing{font-size:11px;color:var(--tx3);margin:0 0 8px}
.ci-missing span{border:1px solid rgba(234,179,8,.45);color:var(--yl);border-radius:999px;padding:1px 8px;margin-left:4px;font-size:10px}
.ci-acts{background:rgba(34,197,94,.06);border:1px solid rgba(34,197,94,.3);border-radius:10px;padding:9px 12px;margin-bottom:8px;font-size:12px;line-height:1.8}
.ci-acts-h{font-size:10px;font-weight:800;letter-spacing:.5px;text-transform:uppercase;color:var(--gn);margin-bottom:2px}
.ci-rel{font-size:11px;color:var(--tx3);margin:2px 0 8px;white-space:pre-wrap}
`;
